import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, Table } from 'antd';
import cx from 'classnames';
import pluralize from 'pluralize';
import moment from 'moment';
import {
  FilterValue,
  SorterResult,
  SortOrder,
  TablePaginationConfig,
} from 'antd/lib/table/interface';
import queryString from 'query-string';
import { useDidUpdate } from 'rooks';

import BasePageLabel from 'components/wrappers/BasePageLabel';
import { pageDefaultSizeTable } from 'constants/index';
import LastSyncDateSection from 'components/LastSyncDateSection';
import SortButton from 'components/wrappers/BaseSortButton';
import {
  getAgingReportTotalBalance,
  getAgingReportTotalBalanceExcel,
} from 'redux/reducers/homeDashboardSlice';
import { useAppDispatch } from 'hooks/useAppDispatch';
import BaseTableHeader from 'components/wrappers/BaseTableHeader';
import {
  isColumnHeightToFullTableHeight,
  isShowFooterTableOnLastPage,
} from 'utils/tableSettings';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import BaseAlert from 'components/wrappers/BaseAlert';
import { downloadFileByObjectURL } from 'utils/fileUtils';
import { SortingDirectionEnum } from 'enums';
import { getSortOrderRequestValue } from 'utils/sortUtils';
import {
  AgingDetailPagedResponse,
  AgingDetailReport,
} from 'api/backofficeApiClient';

import styles from './AgingReportTotalBalancePage.module.less';
import commonStyles from '../UserHomePage.module.less';

export default function AgingReportTotalBalancePage() {
  const { customerKey } = useParams();
  const dispatch = useAppDispatch();
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [
    isAgingReportTotalBalanceDataLoading,
    setIsAgingReportTotalBalanceDataLoading,
  ] = useState(false);
  const [agingReportTotalBalanceData, setAgingReportTotalBalanceData] =
    useState<AgingDetailPagedResponse>();
  const navigate = useNavigate();
  const location = useLocation();
  const { sortOrder } = queryString.parse(location.search);
  const [sortParam, setSortParam] = useState<SortOrder | undefined>(
    sortOrder || SortingDirectionEnum.Ascend,
  );
  const [currentTablePage, setCurrentTablePage] = useState(1);

  useDidUpdate(() => {
    const queryData = {
      sortOrder: sortParam,
    };
    navigate(`${location.pathname}?${queryString.stringify(queryData)}`, {
      replace: true,
    });
    // eslint-disable-next-line
  }, [sortParam]);

  const columns = [
    {
      title: 'Customer Name',
      key: 'customerName',
      className: 'custom-table-cell--union',
      render: (
        record: AgingDetailReport,
        row: AgingDetailReport,
        index: number,
      ) => {
        return {
          props: {
            rowSpan: isColumnHeightToFullTableHeight<AgingDetailReport>(
              index,
              agingReportTotalBalanceData?.details || [],
            ),
          },
          children: (
            <Link
              className="wrapped-text"
              to={`/customer/${agingReportTotalBalanceData?.customerKey}`}
            >
              {agingReportTotalBalanceData?.customerCompanyName}
            </Link>
          ),
        };
      },
    },
    {
      title: 'Customer ID',
      key: 'customerId',
      className: 'custom-table-cell--union',
      render: (
        record: AgingDetailReport,
        row: AgingDetailReport,
        index: number,
      ) => {
        return {
          props: {
            rowSpan: isColumnHeightToFullTableHeight<AgingDetailReport>(
              index,
              agingReportTotalBalanceData?.details || [],
            ),
          },
          children: <span>{agingReportTotalBalanceData?.customerKey}</span>,
        };
      },
    },
    {
      title: 'Terms',
      key: 'terms',
      className: 'custom-table-cell--union',
      render: (
        record: AgingDetailReport,
        row: AgingDetailReport,
        index: number,
      ) => {
        return {
          props: {
            rowSpan: isColumnHeightToFullTableHeight<AgingDetailReport>(
              index,
              agingReportTotalBalanceData?.details || [],
            ),
          },
          children: <span>{agingReportTotalBalanceData?.terms}</span>,
        };
      },
    },
    {
      title: 'Worker Name',
      key: 'workerName',
      render: (record: AgingDetailReport) => record.workerName,
    },
    {
      title: 'Document Number',
      key: 'documentNumber',
      render: (record: AgingDetailReport) => record.invoice,
    },
    {
      title: () => {
        return (
          <SortButton
            isActive={true}
            sortType={sortParam}
            label="Invoice Date"
          />
        );
      },
      key: 'invoiceDate',
      sorter: true,
      sortDirections: [
        SortingDirectionEnum.Ascend,
        SortingDirectionEnum.Descend,
      ],
      sortOrder: sortParam,
      render: (record: AgingDetailReport) =>
        record.invDate ? moment.utc(record.invDate).format('MM/DD/YYYY') : '',
    },
    {
      title: 'Due Date',
      key: 'dueDate',
      render: (record: AgingDetailReport) =>
        record.dueDate ? moment.utc(record.dueDate).format('MM/DD/YYYY') : '',
    },
    {
      title: 'Age',
      key: 'age',
      render: (record: AgingDetailReport) => record.age,
    },
    {
      title: 'Open Balance',
      key: 'openBalance',
      render: (record: AgingDetailReport) => record.balance,
    },
  ];

  const loadAgingReportTotalBalanceData = useCallback(async () => {
    if (!customerKey) return;

    setIsAgingReportTotalBalanceDataLoading(true);

    const result = await dispatch(
      getAgingReportTotalBalance({
        pageNumber: currentTablePage - 1,
        sortType: getSortOrderRequestValue(sortParam),
        pageSize: pageDefaultSizeTable,
        customerKey,
      }),
    );

    if (getAgingReportTotalBalance.fulfilled.match(result)) {
      setAgingReportTotalBalanceData(result.payload);
    } else {
      setAgingReportTotalBalanceData({
        totalCount: 0,
        details: [],
      });
    }

    setIsAgingReportTotalBalanceDataLoading(false);
  }, [dispatch, currentTablePage, customerKey, sortParam]);

  const loadInitialData = useCallback(async () => {
    await loadAgingReportTotalBalanceData();

    setIsInitialDataLoaded(true);
  }, [loadAgingReportTotalBalanceData]);

  const handleDownloadReport = async () => {
    if (!customerKey) return;

    const result = await dispatch(
      getAgingReportTotalBalanceExcel({
        pageNumber: currentTablePage - 1,
        sortType: getSortOrderRequestValue(sortParam),
        pageSize: pageDefaultSizeTable,
        customerKey,
      }),
    );

    if (getAgingReportTotalBalanceExcel.fulfilled.match(result)) {
      downloadFileByObjectURL(result.payload.url, result.payload.fileName);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const agingReportTotalBalanceCountMessage = pluralize(
    'record',
    agingReportTotalBalanceData?.totalCount,
    true,
  );

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filter: Record<string, FilterValue | null>,
    sort: SorterResult<AgingDetailReport> | SorterResult<AgingDetailReport>[],
  ) => {
    const sortOrder =
      (sort as SorterResult<AgingDetailReport>).order ||
      SortingDirectionEnum.Ascend;

    setSortParam((prevSortOrder) => {
      if (prevSortOrder !== sortOrder) {
        setCurrentTablePage(1);
      } else {
        setCurrentTablePage(pagination.current || 1);
      }
      return sortOrder;
    });
  };

  return (
    <div className="page-wrapper scroll-box">
      <div className="page-holder">
        <BasePageLabel label="Dashboard details">
          {agingReportTotalBalanceData?.lastSyncDate && (
            <div className="title-line__info">
              <LastSyncDateSection
                syncDate={agingReportTotalBalanceData.lastSyncDate}
                title="Aging report is automatically refreshed every 10 minutes."
              />
            </div>
          )}
        </BasePageLabel>

        <div className="page-box page-inner">
          <div className="page-inner-container">
            <BaseTableHeader
              title="AR Aging - Total Balance"
              enableSubTitle={isInitialDataLoaded}
              subTitle={agingReportTotalBalanceCountMessage}
            >
              <div>
                <Button
                  className={commonStyles.saveBtn}
                  onClick={handleDownloadReport}
                  icon={<span className="icon icon-xls-file" />}
                />
              </div>
            </BaseTableHeader>
            <Table
              className={cx(styles.table, 'custom-table')}
              pagination={{
                total: agingReportTotalBalanceData?.totalCount,
                pageSize: pageDefaultSizeTable,
                current: currentTablePage,
                onChange: setCurrentTablePage,
                showSizeChanger: false,
              }}
              showSorterTooltip={false}
              summary={() => {
                if (
                  !isInitialDataLoaded ||
                  !isShowFooterTableOnLastPage(
                    agingReportTotalBalanceData?.totalCount,
                    pageDefaultSizeTable,
                    agingReportTotalBalanceData?.pageNumber,
                  )
                )
                  return;

                return (
                  <Table.Summary.Row>
                    <Table.Summary.Cell colSpan={8} index={1}>
                      Total
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>
                      {agingReportTotalBalanceData?.totalBalance}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                );
              }}
              onChange={handleTableChange}
              columns={columns}
              dataSource={agingReportTotalBalanceData?.details}
              rowKey={(record) => record.invoice as string}
              loading={isAgingReportTotalBalanceDataLoading}
              scroll={{ x: '100%' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
